import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { coulombMetricsService } from '../../../services/coulombMetricsService';
import { processCoulombData } from '../../../utils/coulombDataProcessor';

const timeFilters = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' }
];

const metricOptions = [
  { value: 'battery_current', label: 'Battery Coulomb' },
  { value: 'shunt_current', label: 'Shunt Coulomb' }
];

const CoulombMetrics = ({ emsName }) => {
  const [timeFilter, setTimeFilter] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState('battery_current');

  const fetchData = useCallback(async () => {
    if (!emsName) return;
    
    try {
      setLoading(true);
      setError(null);
      const formattedDate = selectedDate.toLocaleDateString('en-CA');
      const response = await coulombMetricsService.getCoulombMetrics(
        emsName,
        timeFilter,
        formattedDate
      );
      
      const processedData = processCoulombData(response.data);
      setData(processedData);
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message || 'Failed to load coulomb metrics');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [emsName, timeFilter, selectedDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatXAxis = useCallback((timestamp) => {
    const date = new Date(timestamp);
    switch(timeFilter) {
      case 'monthly':
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      case 'weekly':
        return date.toLocaleDateString('en-US', { weekday: 'short' });
      case 'daily':
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      default:
        return date.toLocaleString();
    }
  }, [timeFilter]);

  const renderChart = useCallback(() => {
    if (!data.length) return null;

    return (
      <Box sx={{ width: 900, height: 400 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="timestamp"
              tickFormatter={formatXAxis}
              type="number"
              domain={['auto', 'auto']}
              scale="time"
            />
            <YAxis 
              domain={['auto', 'auto']}
              tickFormatter={value => `${value.toFixed(2)} A`}
            />
            <Tooltip 
              labelFormatter={(label) => new Date(label).toLocaleString()}
              formatter={(value, name) => {
                const label = value < 0 ? 'Coulomb In' : 'Coulomb Out';
                const source = selectedMetric === 'battery_current' ? 'Battery' : 'Shunt';
                return [`${Math.abs(value).toFixed(2)} A`, `${source} ${label}`];
              }}
            />
            <Legend 
              formatter={(value) => {
                return value === 'battery_current' ? 'Battery Coulomb' : 'Shunt Coulomb';
              }}
            />
            <Line 
              type="monotone" 
              dataKey={selectedMetric}
              name={selectedMetric === 'battery_current' ? 'Battery Coulomb' : 'Shunt Coulomb'}
              stroke="#8884d8" 
              dot={false}
              isAnimationActive={false}
              strokeWidth={2}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    );
  }, [data, selectedMetric, formatXAxis]);

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ width: 'fit-content' }}>
      <Box sx={{ 
        display: 'flex',
        gap: 2,
        mb: 2,
        p: 2,
        bgcolor: 'background.paper',
        borderRadius: 1,
        justifyContent: 'flex-start',
        '& .MuiFormControl-root': {
          minWidth: 180
        }
      }}>
        <FormControl size="small">
          <InputLabel>Metric</InputLabel>
          <Select
            value={selectedMetric}
            label="Metric"
            onChange={(e) => setSelectedMetric(e.target.value)}
            MenuProps={{
                sx: {
                  '& .MuiMenu-paper': {
                    '& .MuiMenuItem-root': {
                      padding: '8px 16px',
                      margin: '4px 0',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }
                  }
                }
              }}
            
          >
            {metricOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small">
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeFilter}
            label="Time Range"
            onChange={(e) => setTimeFilter(e.target.value)}
            MenuProps={{
              sx: {
                '& .MuiMenu-paper': {
                  '& .MuiMenuItem-root': {
                    padding: '8px 16px',
                    margin: '4px 0',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }
                }
              }
            }}
          >
            {timeFilters.map((filter) => (
              <MenuItem key={filter.value} value={filter.value}>
                {filter.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Select Date"
            value={selectedDate}
            onChange={(date) => date && setSelectedDate(date)}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true
              }
            }}
          />
        </LocalizationProvider>
      </Box>

      {data.length > 0 ? (
        <Box sx={{ width: '100%' }}>
          <Typography variant="subtitle1" gutterBottom>
            {selectedMetric === 'battery_current' ? 'Battery Coulomb' : 'Shunt Coulomb'} Metrics
          </Typography>
          {renderChart()}
        </Box>
      ) : (
        <Alert severity="info">No data available for the selected period</Alert>
      )}
    </Box>
  );
};

export default React.memo(CoulombMetrics);